import { useMemo } from 'react';

import {
  MarkdownProse,
  PlanShapeBadge,
  PlanSpecView,
} from '@eluve/care-plan-render';
import {
  Badge,
  Button,
  Caption,
  Heading,
  Icon,
  LoadingIcon,
  Skeleton,
  Text,
} from '@eluve/components';
import { PLAN_SHAPES, type PlanDraft, type PlanSpec } from '@eluve/plan-shapes';

import { FhirJsonDialog } from './FhirJsonDialog';
import { planSaveBundle } from './care-plans';

// The in-flight emission: presentPlan streams the complete spec as its tool
// input, so while it streams we have a growing, coerced prefix of the draft.
// `stage` distinguishes the model still writing the spec from the Medplum
// $validate round-trip that follows.
export type DraftingState = {
  plan: PlanSpec | null;
  stage: 'drafting' | 'validating';
  // A re-emission after a prior draft settled invalid: the agent is working
  // through the $validate issues, not drafting fresh. `issueCount` is the count
  // from that prior invalid emission.
  resolvingIssues?: boolean;
  issueCount?: number;
};

// The persistent preview pane of the Plan Builder: renders the living draft —
// the latest presentPlan emission — through its shape's render primitive,
// with the $validate verdict and the human-driven Save. While an emission is
// still streaming, the draft builds here LIVE instead of behind a spinner.
// The agent never writes the resources; the Save button does (browser-side,
// admin token).
export function PlanDraftPreview({
  draft,
  drafting,
  onSave,
  saving,
}: {
  draft: PlanDraft | null;
  drafting: DraftingState | null;
  onSave: (draft: PlanDraft) => void;
  saving: boolean;
}) {
  // A streaming emission takes the pane over as soon as it has anything
  // renderable; before that, fall back to the previous draft if one exists
  // (re-emissions shouldn't flash a skeleton over a full draft).
  if (drafting && (drafting.plan || !draft)) {
    return <DraftingPane drafting={drafting} />;
  }

  if (!draft) {
    return (
      <div className="grid h-full place-items-center px-8 text-center">
        <div className="space-y-2">
          <Icon
            name="ClipboardList"
            size="auto"
            className="mx-auto size-8 text-muted-foreground/50"
          />
          <Text>No draft yet</Text>
          <Text size="xs" color="secondary" className="max-w-xs">
            Describe a care journey in the chat — the living draft renders here
            as the Plan Builder shapes it.
          </Text>
        </div>
      </div>
    );
  }

  const spec = draft.plan;
  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 p-5">
          <SpecHeader spec={spec} />

          <ValidationBadge draft={draft} />

          <PlanSpecView spec={spec} />

          <FhirPreview draft={draft} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
        <Text size="xs" color="secondary">
          Saving publishes this template (status{' '}
          <Text inline color="secondary" className="font-mono">
            active
          </Text>
          ).
        </Text>
        <Button onClick={() => onSave(draft)} disabled={saving || !draft.valid}>
          <Icon name="Save" size="auto" />
          {saving ? 'Saving…' : 'Save as Plan Definition'}
        </Button>
      </div>
    </div>
  );
}

// Title block shared by the settled draft and the live-building view.
function SpecHeader({ spec }: { spec: PlanSpec }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <PlanShapeBadge shape={spec.shape} />
        <Text inline size="xs" color="secondary">
          {PLAN_SHAPES[spec.shape].blurb}
        </Text>
      </div>
      <Heading size="lg" weight="semibold" level={2} className="mt-2">
        {spec.title}
      </Heading>
      {spec.description && (
        <MarkdownProse
          content={spec.description}
          className="mt-1 text-sm text-muted-foreground"
        />
      )}
      {spec.condition && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Caption inline color="secondary">
            Condition
          </Caption>
          <Badge variant="secondary">
            {spec.condition.text}
            {spec.condition.code && (
              <Text inline color="secondary" className="ml-1 font-mono">
                {spec.condition.code}
              </Text>
            )}
          </Badge>
        </div>
      )}
    </div>
  );
}

// The emission building live: the coerced prefix of the spec renders through
// the same shape primitives as the settled draft, growing as tokens stream —
// new phases/activities pop in, trailing skeleton hints at more to come.
// Deliberately NO auto-scroll: the reader keeps control of the viewport
// while content grows below.
function DraftingPane({ drafting }: { drafting: DraftingState }) {
  const spec = drafting.plan;

  if (!spec) {
    return (
      <div className="grid h-full place-items-center px-8">
        <div className="w-full max-w-sm space-y-5 text-center">
          <div className="mx-auto grid size-12 place-items-center rounded-2xl bg-bg-strong-primary text-text-positive-strong">
            <Icon
              name="Sparkles"
              size="auto"
              className="size-6 animate-pulse"
            />
          </div>
          <div className="space-y-1">
            <Text>
              The Plan Builder is shaping your plan
              <LoadingIcon
                size="auto"
                className="ml-1 inline size-3.5 align-middle text-text-strong"
              />
            </Text>
            <Text size="xs" color="secondary">
              The draft builds here live as it streams.
            </Text>
          </div>
          <div className="space-y-2 text-left">
            <Skeleton className="h-8 w-2/3 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl opacity-60" />
            <Skeleton className="h-20 rounded-xl opacity-30" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 p-5">
          <SpecHeader spec={spec} />
          <DraftingBanner drafting={drafting} />
          <PlanSpecView spec={spec} />
          {drafting.stage === 'drafting' && (
            <Skeleton className="h-16 rounded-xl opacity-60" />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
        <Text size="xs" color="secondary">
          {drafting.resolvingIssues && drafting.stage === 'drafting'
            ? 'Resolving validation issues — re-drafting…'
            : drafting.stage === 'drafting'
              ? 'Drafting — the plan is still streaming in.'
              : 'Validating the draft against Medplum…'}
        </Text>
        <Button disabled>
          <Icon name="Save" size="auto" />
          Save as Plan Definition
        </Button>
      </div>
    </div>
  );
}

function DraftingBanner({ drafting }: { drafting: DraftingState }) {
  const { stage, resolvingIssues, issueCount } = drafting;
  const count = issueCount ?? 0;
  const message =
    resolvingIssues && stage === 'drafting'
      ? `Resolving ${count} validation issue${count === 1 ? '' : 's'} — re-drafting`
      : stage === 'drafting'
        ? 'Drafting — the plan builds live as it streams'
        : 'Draft complete — validating against Medplum';
  return (
    <div className="flex items-center gap-2 rounded-md bg-bg-strong-primary px-3 py-2 text-xs text-text-strong">
      <Icon name="Sparkles" size="auto" className="size-4 shrink-0" />
      <span>{message}</span>
      <LoadingIcon size="auto" className="ml-0.5 size-4 shrink-0" />
    </div>
  );
}

// The $validate verdict for the current emission: pre-proven valid means save
// is guaranteed to insert ($validate runs the same strict validation as create).
function ValidationBadge({ draft }: { draft: PlanDraft }) {
  if (draft.valid) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border-positive-subtle bg-bg-positive-subtle px-3 py-2 text-xs text-text-positive-subtle">
        <Icon name="CheckCircle2" size="auto" className="size-4 shrink-0" />
        Valid FHIR, ready to save.
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-md border border-border-warning-subtle bg-bg-warning-subtle px-3 py-2 text-xs text-text-warning-subtle">
      <p className="flex items-center gap-2 font-medium">
        <Icon name="AlertTriangle" size="auto" className="size-4 shrink-0" />
        Validation failed — the Plan Builder will fix and re-emit before this
        can be saved.
      </p>
      <ul className="space-y-0.5 pl-6">
        {draft.issues.map((issue, i) => (
          <li key={i} className="font-mono break-all">
            {issue.resource}: {issue.details}
            {issue.expression ? ` @ ${issue.expression}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FhirPreview({ draft }: { draft: PlanDraft }) {
  // The exact resources Save inserts — same deterministic compile both places.
  const bundle = useMemo(() => planSaveBundle(draft.plan), [draft.plan]);
  const count = bundle.entry?.length ?? 0;
  return (
    <FhirJsonDialog
      resource={bundle}
      title="Preview FHIR"
      description="The exact transaction Bundle Save will post — the PlanDefinition plus its ActivityDefinitions, compiled deterministically from the current draft."
      trigger={
        <Button variant="outline" size="xs" className="w-full justify-start">
          <Icon name="Code2" size="auto" />
          Preview FHIR
          <Text inline size="xs" color="secondary">
            · the {count === 1 ? 'PlanDefinition' : `${count} resources`} Save
            will create
          </Text>
          <Icon name="ChevronRight" size="auto" className="ml-auto" />
        </Button>
      }
    />
  );
}
